import { useEffect, useSyncExternalStore } from "react";
import { stripBase } from "./appMode";

/**
 * The search overlay's open state. Module store in the activeServer idiom —
 * NOT router state, so the page underneath stays mounted and keeps its
 * scroll position while the overlay is up. Openers: the bar's search icon,
 * Cmd/Ctrl+K, and the /search deep-link redirect (which passes its ?q=).
 */

interface SearchOverlayState {
  open: boolean;
  /** Seed for the overlay's input — the overlay owns edits locally. */
  query: string;
}

let state: SearchOverlayState = { open: false, query: "" };
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

export function openSearchOverlay(query?: string): void {
  state = { open: true, query: query ?? state.query };
  emit();
}

export function closeSearchOverlay(): void {
  if (!state.open) return;
  state = { ...state, open: false };
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

const snapshot = (): SearchOverlayState => state;

export function useSearchOverlay(): SearchOverlayState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * Cmd/Ctrl+K opens the overlay anywhere but the player (its z-50 takeover
 * would bury a z-40 layer, and its own key handling must win). Mounted once
 * in the authed layout.
 */
export function useSearchHotkey(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "k") return;
      if (stripBase(window.location.pathname).startsWith("/player")) return;
      e.preventDefault();
      openSearchOverlay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
